import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useDepartamento } from '../../contexts/DepartamentoContext';
import { Usuario } from '../../types';
import api from '../../services/api';

const Profile: React.FC = () => {
  const { usuario, atualizarPerfil, atualizarAvatar } = useAuth();
  const { obterDepartamentoPorId, departamentos } = useDepartamento();
  const [nomeDepartamento, setNomeDepartamento] = useState('');
  const [ehAdmin, setEhAdmin] = useState(false);
  const [modoEdicao, setModoEdicao] = useState(false);
  const [dadosUsuario, setDadosUsuario] = useState({
    nome: usuario?.nome || '',
    email: usuario?.email || '',
    cargo: usuario?.cargo || '',
    departamentoId: usuario?.departamentoId || '',
    avatar: usuario?.avatar || '',
    senha_app_gmail: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [mensagem, setMensagem] = useState<{texto: string, tipo: 'sucesso' | 'erro'} | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [uploadandoAvatar, setUploadandoAvatar] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Buscar dados atualizados do backend
  useEffect(() => {
    const buscarDadosAtualizados = async () => {
      if (usuario) {
        try {
          const response = await api.get('/auth/me');
          if (response.data.success && response.data.usuario) {
            const usuarioAtualizado = response.data.usuario;

            setEhAdmin(usuarioAtualizado.tipo === 'admin');

            setDadosUsuario({
              nome: usuarioAtualizado.nome || '',
              email: usuarioAtualizado.email || '',
              cargo: usuarioAtualizado.cargo || '',
              departamentoId: usuarioAtualizado.departamento_id || usuarioAtualizado.departamentoId || '',
              avatar: usuarioAtualizado.avatar || '',
              senha_app_gmail: usuarioAtualizado.senha_app_gmail || ''
            });

            const depId = usuarioAtualizado.departamento_id || usuarioAtualizado.departamentoId;
            if (depId) {
              const departamento = obterDepartamentoPorId(depId);
              if (departamento) setNomeDepartamento(departamento.nome);
              else setNomeDepartamento('Não definido');
            }
          }
        } catch (error) {
          console.error('Erro ao buscar dados do usuário:', error);
          setEhAdmin(usuario.tipo === 'admin');
          setDadosUsuario({
            nome: usuario.nome || '',
            email: usuario.email || '',
            cargo: usuario.cargo || '',
            departamentoId: usuario.departamento_id || usuario.departamentoId || '',
            avatar: usuario.avatar || '',
            senha_app_gmail: usuario.senha_app_gmail || ''
          });
        }
      }
    };

    buscarDadosAtualizados();
  }, [usuario, obterDepartamentoPorId]);

  useEffect(() => {
    if (dadosUsuario.departamentoId) {
      const departamento = obterDepartamentoPorId(dadosUsuario.departamentoId);
      if (departamento) setNomeDepartamento(departamento.nome);
    }
  }, [dadosUsuario.departamentoId, obterDepartamentoPorId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setDadosUsuario(prev => ({ ...prev, [name]: value }));
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Preview imediato com object URL
    const previewUrl = URL.createObjectURL(file);
    setAvatarPreview(previewUrl);

    setUploadandoAvatar(true);
    try {
      const formData = new FormData();
      formData.append('avatar', file);

      const response = await api.post('/auth/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        URL.revokeObjectURL(previewUrl);
        atualizarAvatar(response.data.avatar);
        setDadosUsuario(prev => ({ ...prev, avatar: response.data.avatar }));
        setAvatarPreview(null);
        setMensagem({ texto: 'Foto de perfil atualizada com sucesso!', tipo: 'sucesso' });
      } else {
        URL.revokeObjectURL(previewUrl);
        setAvatarPreview(null);
        setMensagem({ texto: response.data.message || 'Erro ao salvar foto.', tipo: 'erro' });
      }
    } catch (error: any) {
      URL.revokeObjectURL(previewUrl);
      setAvatarPreview(null);
      setMensagem({ texto: error.response?.data?.message || 'Erro ao enviar foto.', tipo: 'erro' });
    } finally {
      setUploadandoAvatar(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMensagem(null);

    try {
      const dadosParaAtualizar: Partial<Usuario> = {
        nome: dadosUsuario.nome,
        email: dadosUsuario.email,
        cargo: dadosUsuario.cargo,
        senha_app_gmail: dadosUsuario.senha_app_gmail
      };

      if (ehAdmin && dadosUsuario.departamentoId) {
        dadosParaAtualizar.departamento_id = dadosUsuario.departamentoId;
      }

      const sucesso = await atualizarPerfil(dadosParaAtualizar);

      if (sucesso) {
        setMensagem({ texto: 'Perfil atualizado com sucesso!', tipo: 'sucesso' });
        setModoEdicao(false);

        const depId = usuario?.departamento_id || usuario?.departamentoId;
        if (depId) {
          const departamento = obterDepartamentoPorId(depId);
          if (departamento) setNomeDepartamento(departamento.nome);
        }
      } else {
        setMensagem({ texto: 'Erro ao atualizar o perfil. Tente novamente.', tipo: 'erro' });
      }
    } catch (error) {
      console.error('Erro ao atualizar perfil:', error);
      setMensagem({ texto: 'Ocorreu um erro ao processar sua solicitação.', tipo: 'erro' });
    } finally {
      setIsLoading(false);
    }
  };

  if (!usuario) {
    return <div>Carregando...</div>;
  }

  const avatarSrc = avatarPreview || dadosUsuario.avatar || usuario.avatar || '/profilepicture.png';

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">Perfil do Usuário</h1>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="md:flex">
          <div className="md:w-1/3 bg-gray-50 p-8 flex flex-col items-center">

            {/* Avatar clicável */}
            <div className="relative mb-4 group cursor-pointer" onClick={handleAvatarClick}>
              <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-white shadow-md">
                <img
                  src={avatarSrc}
                  alt="Avatar"
                  className="w-full h-full object-cover"
                  onError={(e) => { (e.target as HTMLImageElement).src = '/profilepicture.png'; }}
                />
              </div>
              {/* Overlay ao passar o mouse */}
              <div className="absolute inset-0 rounded-full bg-black bg-opacity-40 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                {uploadandoAvatar ? (
                  <svg className="w-6 h-6 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                ) : (
                  <>
                    <svg className="w-6 h-6 text-white mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="text-white text-xs font-medium">Alterar foto</span>
                  </>
                )}
              </div>
            </div>

            {/* Input oculto para seleção de arquivo */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              style={{ display: 'none' }}
              onChange={handleAvatarChange}
            />

            <p className="text-xs text-gray-400 mb-4">Clique na foto para alterar</p>

            <h2 className="text-xl font-semibold">{usuario.nome}</h2>
            <p className="text-gray-600">{usuario.cargo}</p>
            <p className="text-gray-500 text-sm">{nomeDepartamento}</p>

            <div className="mt-6 w-full">
              <div className="bg-white rounded-lg shadow p-4 mb-4">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Estatísticas</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-bravo-primary">{usuario.tarefas_ativas || usuario.tarefasAtivas || 0}</p>
                    <p className="text-xs text-gray-500">Tarefas Ativas</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600">{usuario.tarefas_concluidas || usuario.tarefasConcluidas || 0}</p>
                    <p className="text-xs text-gray-500">Tarefas Concluídas</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-4">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Informações</h3>
                <div className="space-y-2">
                  <div>
                    <p className="text-xs text-gray-500">Membro desde</p>
                    <p className="text-sm">{(usuario.data_criacao || usuario.dataCriacao) ? new Date(usuario.data_criacao || usuario.dataCriacao || '').toLocaleDateString('pt-BR') : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Email</p>
                    <p className="text-sm">{usuario.email}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="md:w-2/3 p-8">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold">Informações do Perfil</h3>
              {!modoEdicao && (
                <button
                  onClick={() => setModoEdicao(true)}
                  className="px-4 py-2 bg-bravo-primary text-white rounded hover:bg-blue-600"
                >
                  Editar Perfil
                </button>
              )}
            </div>

            {mensagem && (
              <div className={`p-4 mb-6 rounded ${mensagem.tipo === 'sucesso' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                {mensagem.texto}
              </div>
            )}

            {modoEdicao ? (
              <form onSubmit={handleSubmit}>
                <div className="space-y-6">
                  <div>
                    <label htmlFor="nome" className="block text-sm font-medium text-gray-700 mb-1">Nome Completo</label>
                    <input
                      type="text"
                      name="nome"
                      id="nome"
                      value={dadosUsuario.nome}
                      onChange={handleChange}
                      required
                      className="w-full p-2 border rounded focus:ring-bravo-primary focus:border-bravo-primary"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      value={dadosUsuario.email}
                      onChange={handleChange}
                      required
                      className="w-full p-2 border rounded focus:ring-bravo-primary focus:border-bravo-primary"
                    />
                  </div>

                  <div>
                    <label htmlFor="cargo" className="block text-sm font-medium text-gray-700 mb-1">Cargo</label>
                    {ehAdmin ? (
                      <input
                        type="text"
                        name="cargo"
                        id="cargo"
                        value={dadosUsuario.cargo}
                        onChange={handleChange}
                        required
                        className="w-full p-2 border rounded focus:ring-bravo-primary focus:border-bravo-primary"
                      />
                    ) : (
                      <>
                        <input type="text" name="cargo" value={dadosUsuario.cargo} disabled className="w-full p-2 border rounded bg-gray-100 text-gray-700" />
                        <p className="text-xs text-gray-500 mt-1">O cargo só pode ser alterado pelo administrador do sistema.</p>
                      </>
                    )}
                  </div>

                  <div>
                    <label htmlFor="departamento" className="block text-sm font-medium text-gray-700 mb-1">Departamento</label>
                    {ehAdmin ? (
                      <>
                        <select
                          name="departamentoId"
                          id="departamento"
                          value={dadosUsuario.departamentoId}
                          onChange={(e) => setDadosUsuario(prev => ({ ...prev, departamentoId: e.target.value }))}
                          className="w-full p-2 border rounded focus:ring-bravo-primary focus:border-bravo-primary"
                        >
                          <option value="">Selecione um departamento</option>
                          {departamentos.map(dep => (
                            <option key={dep.id} value={dep.id}>{dep.nome}</option>
                          ))}
                        </select>
                        <p className="text-xs text-green-600 mt-1">Como administrador, você pode alterar o departamento.</p>
                      </>
                    ) : (
                      <>
                        <input type="text" name="departamento" value={nomeDepartamento} disabled className="w-full p-2 border rounded bg-gray-100 text-gray-700" />
                        <p className="text-xs text-gray-500 mt-1">O departamento só pode ser alterado pelo administrador do sistema.</p>
                      </>
                    )}
                  </div>

                  <div className="senha-app-gmail">
                    <label htmlFor="senha_app_gmail" className="block text-sm font-medium text-gray-700 mb-1">Senha de App do Gmail</label>
                    <input
                      type="password"
                      name="senha_app_gmail"
                      id="senha_app_gmail"
                      value={dadosUsuario.senha_app_gmail || ''}
                      onChange={handleChange}
                      className="w-full p-2 border rounded focus:ring-bravo-primary focus:border-bravo-primary"
                      placeholder="Digite sua senha de app do Gmail"
                    />
                    <div className="mt-2 p-3 bg-blue-50 rounded-md">
                      <h4 className="text-sm font-medium text-blue-800 mb-2">Como obter uma senha de app do Gmail?</h4>
                      <ol className="text-xs text-blue-700 list-decimal pl-4 space-y-2">
                        <li>
                          <strong>Acesse sua conta Google:</strong>
                          <a href="https://myaccount.google.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline ml-1 hover:text-blue-800">Clique aqui para acessar sua Conta Google</a>
                        </li>
                        <li>
                          <strong>Ative a Verificação em duas etapas:</strong>
                          <a href="https://myaccount.google.com/signinoptions/two-step-verification" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline ml-1 hover:text-blue-800">Clique aqui para ativar</a>
                          <span className="block mt-1 text-gray-600">⚠️ Este passo é obrigatório para gerar senhas de app</span>
                        </li>
                        <li>
                          <strong>Gere uma senha de app:</strong>
                          <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline ml-1 hover:text-blue-800">Clique aqui para criar uma senha de app</a>
                        </li>
                        <li>
                          <strong>Configure a senha de app:</strong>
                          <ul className="list-disc pl-4 mt-1 space-y-1">
                            <li>Digite "BravoHub" no campo de nome quando solicitado</li>
                            <li>Clique em "Criar"</li>
                            <li>Copie a senha gerada de 16 caracteres (sem espaços) e cole no campo acima</li>
                            <li>Clique em "Concluído" para finalizar</li>
                          </ul>
                        </li>
                      </ol>
                      <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                        <p className="text-xs text-gray-700">
                          <strong className="text-yellow-700">ℹ️ Importante:</strong> Esta senha será usada para enviar notificações por email através da sua conta Gmail. Suas credenciais são armazenadas com segurança e nunca são compartilhadas.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setModoEdicao(false)}
                      className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={isLoading}
                      className={`px-4 py-2 text-white rounded ${isLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-bravo-primary hover:bg-blue-600'}`}
                    >
                      {isLoading ? 'Salvando...' : 'Salvar Alterações'}
                    </button>
                  </div>
                </div>
              </form>
            ) : (
              <div className="space-y-6">
                <div className="border-b pb-4">
                  <h4 className="text-sm font-medium text-gray-500 mb-1">Nome Completo</h4>
                  <p>{usuario.nome}</p>
                </div>

                <div className="border-b pb-4">
                  <h4 className="text-sm font-medium text-gray-500 mb-1">Email</h4>
                  <p>{usuario.email}</p>
                </div>

                <div className="border-b pb-4">
                  <h4 className="text-sm font-medium text-gray-500 mb-1">Cargo</h4>
                  <p>{usuario.cargo}</p>
                </div>

                <div className="border-b pb-4">
                  <h4 className="text-sm font-medium text-gray-500 mb-1">Departamento</h4>
                  <p>{nomeDepartamento || 'Não definido'}</p>
                </div>

                <div className="border-b pb-4">
                  <h4 className="text-sm font-medium text-gray-500 mb-1">Senha de App do Gmail</h4>
                  <p>{dadosUsuario.senha_app_gmail ? '••••••••••••••••' : 'Não configurada'}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {dadosUsuario.senha_app_gmail
                      ? 'Você já configurou uma senha de app para envio de emails.'
                      : 'Configure uma senha de app do Gmail para enviar notificações através da sua conta.'}
                  </p>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-1">Preferências de Notificação</h4>
                  <div className="flex items-center mt-2">
                    <input id="email-notif" type="checkbox" className="h-4 w-4 text-bravo-primary focus:ring-bravo-primary border-gray-300 rounded" defaultChecked />
                    <label htmlFor="email-notif" className="ml-2 block text-sm text-gray-900">Receber notificações por email</label>
                  </div>
                  <div className="flex items-center mt-2">
                    <input id="browser-notif" type="checkbox" className="h-4 w-4 text-bravo-primary focus:ring-bravo-primary border-gray-300 rounded" defaultChecked />
                    <label htmlFor="browser-notif" className="ml-2 block text-sm text-gray-900">Receber notificações no navegador</label>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
